import { type FC, type ReactNode, useMemo, useState } from "react";

import { Text } from "../common/Text";

export interface DonutChartSegment {
  label: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  data: DonutChartSegment[];
  size?: number;
  strokeWidth?: number;
  showLegend?: boolean;
  centerLabel?: ReactNode;
  className?: string;
  onSegmentClick?: (segment: DonutChartSegment) => void;
}

/**
 * Categorical palette — a sequence of maximally-distinct hues assigned by
 * position to give each donut/legend segment its own color. This is NOT the
 * semantic `SIGNAL_HEX` set (healthy/warn/danger): a segment's color carries no
 * meaning beyond "different from its neighbors", so the list is chosen for
 * contrast, not status. Index wraps modulo the palette length.
 */
const CATEGORICAL_PALETTE = [
  "#10b981", // Emerald-500
  "#6366f1", // Indigo-500
  "#8b5cf6", // Violet-500
  "#f59e0b", // Amber-500
  "#ef4444", // Red-500
  "#06b6d4", // Cyan-500
  "#ec4899", // Pink-500
  "#84cc16", // Lime-500
];

export function getChartColor(index: number): string {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}

export const DonutChart: FC<DonutChartProps> = ({
  data,
  size = 160,
  strokeWidth = 24,
  showLegend = true,
  centerLabel,
  className = "",
  onSegmentClick,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const total = useMemo(
    () => data.reduce((sum, item) => sum + item.value, 0),
    [data],
  );

  const segments = useMemo(() => {
    if (total === 0) {
      return [];
    }

    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    let currentOffset = 0;

    return data.map((item, index) => {
      const percentage = (item.value / total) * 100;
      const dashLength = (percentage / 100) * circumference;
      const dashArray = `${dashLength} ${circumference - dashLength}`;
      const rotation = currentOffset;

      currentOffset += percentage * 3.6; // Convert percentage to degrees

      return {
        ...item,
        circumference,
        dashArray,
        index,
        percentage,
        radius,
        rotation,
      };
    });
  }, [data, total, size, strokeWidth]);

  const center = size / 2;

  if (data.length === 0 || total === 0) {
    return (
      <div
        className={`flex flex-col items-center gap-3 ${className}`}
        style={{ width: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90 transform"
        >
          <circle
            cx={center}
            cy={center}
            r={(size - strokeWidth) / 2}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-100 dark:text-white/10"
          />
        </svg>
        <Text>No data</Text>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="relative" style={{ height: size, width: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90 transform"
        >
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={(size - strokeWidth) / 2}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-100 dark:text-white/10"
          />

          {/* Segments */}
          {segments.map((segment) => {
            const isHovered = hoveredIndex === segment.index;

            return (
              <circle
                key={segment.label}
                cx={center}
                cy={center}
                r={segment.radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={isHovered ? strokeWidth + 4 : strokeWidth}
                strokeDasharray={segment.dashArray}
                strokeDashoffset={0}
                strokeLinecap="butt"
                className={`transition-all duration-200 ${onSegmentClick ? "cursor-pointer" : ""}`}
                style={{
                  opacity: hoveredIndex !== null && !isHovered ? 0.5 : 1,
                  transform: `rotate(${segment.rotation}deg)`,
                  transformOrigin: "center",
                }}
                onMouseEnter={() => setHoveredIndex(segment.index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => onSegmentClick?.(segment)}
              />
            );
          })}
        </svg>

        {/* Center label */}
        {centerLabel && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">{centerLabel}</div>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
          {segments.map((segment) => {
            const isHovered = hoveredIndex === segment.index;

            return (
              <div
                key={segment.label}
                className={`flex items-center gap-1.5 text-xs transition-opacity ${
                  hoveredIndex !== null && !isHovered ? "opacity-50" : ""
                }`}
                onMouseEnter={() => setHoveredIndex(segment.index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="text-slate-600 dark:text-slate-300">
                  {segment.label}
                </span>
                <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                  {segment.percentage.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Hover tooltip */}
      {hoveredIndex !== null && segments[hoveredIndex] && (
        <Text as="div" className="mt-1 text-center">
          {segments[hoveredIndex].label}:{" "}
          <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
            {segments[hoveredIndex].percentage.toFixed(1)}%
          </span>
        </Text>
      )}
    </div>
  );
};
